/**
 * My analysis credits.
 *
 * Lists the signed-in client's available analysis credits (tokens), grouped so
 * each clinic-agent pair shows once with a count — no repeated rows. Per group:
 * agent name, clinic name, clinic code, latest purchase date, and how many
 * credits remain for that clinic-agent pair.
 */
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventIcon from '@mui/icons-material/Event';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TokenIcon from '@mui/icons-material/Token';
import { userApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';
import { formatDate } from '@/utils/date';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';

function CreditRow({ icon, label, value, dir }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600} dir={dir} sx={{ wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  );
}

export default function CreditsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  const { data: credits, isLoading, isError } = useQuery({
    queryKey: ['agent-credits'],
    queryFn: () => userApi.getCredits(),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={handleBack} edge="start" aria-label={t('common.back')}>
            {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </IconButton>
          <Box>
            <Typography variant="h6" component="h1" fontWeight={800}>{t('credits.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('credits.subtitle')}</Typography>
          </Box>
        </Stack>

        {isLoading && (
          <Stack spacing={2}>
            {[0, 1].map((i) => <Skeleton key={i} variant="rounded" height={150} />)}
          </Stack>
        )}

        {!isLoading && isError && <Alert severity="error">{t('common.error')}</Alert>}

        {!isLoading && !isError && credits?.length === 0 && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ py: 3 }}>
                <TokenIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography fontWeight={700}>{t('credits.emptyTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('credits.emptyBody')}</Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && credits?.length > 0 && (
          <Stack spacing={2}>
            {credits.map((c) => (
              <Card key={`${c.clinicCode}:${c.agentKey}`} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <SmartToyIcon color="primary" />
                      <Typography fontWeight={800} noWrap>
                        {localized(c.agentName, language) || c.agentKey}
                      </Typography>
                    </Stack>
                    <Chip
                      color="primary"
                      label={t('credits.available', { count: c.available })}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={1}>
                    <CreditRow
                      icon={<StorefrontIcon fontSize="small" />}
                      label={t('credits.clinic')}
                      value={localized(c.clinicName, language) || c.clinicCode}
                    />
                    <CreditRow
                      icon={<ConfirmationNumberIcon fontSize="small" />}
                      label={t('credits.clinicCode')}
                      value={c.clinicCode}
                      dir="ltr"
                    />
                    <CreditRow
                      icon={<EventIcon fontSize="small" />}
                      label={t('credits.purchased')}
                      value={c.purchasedAt ? formatDate(c.purchasedAt, language, { style: 'long' }) : '—'}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
